import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { ColourSwatch } from '../../../components/design-system/common/ColourSwatch';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import {
    NETWORK_APPEARANCES,
    NETWORK_COLOR_FAMILIES,
    NETWORK_FINISHES,
    NETWORK_MATERIALS,
    type ProductInput,
} from '../../../common/hooks/network-admin.types';
import type { NetworkCollection, NetworkProduct } from '../../../common/models/network.model';
import { ErrorNote, FIELD_LABEL, vocabLabel } from './network-parts';
import { splitList } from './network-helpers';

const UNITS = ['cm', 'mm', 'in'];

interface Draft {
    name: string;
    ref: string;
    eans: string;
    collectionId: string;
    width: string;
    height: string;
    depth: string;
    unit: string;
    finish: string;
    colourName: string;
    colourHex: string;
    colourFamily: string;
    material: string;
    appearance: string;
    pattern: string;
    tags: string;
}

const str = (v: unknown) => (v === undefined || v === null ? '' : String(v));

const toDraft = (p?: NetworkProduct | null, collectionId?: string): Draft => ({
    name: str(p?.name),
    ref: str(p?.ref),
    eans: (p?.eans || []).join(', '),
    collectionId: str(p?.collectionId ?? collectionId),
    width: str(p?.format?.width),
    height: str(p?.format?.height),
    depth: str(p?.format?.depth),
    unit: p?.format?.unit || 'cm',
    finish: str(p?.finish),
    colourName: str(p?.colour?.name),
    colourHex: str(p?.colour?.hex),
    colourFamily: str(p?.colour?.family),
    material: str(p?.material),
    appearance: str(p?.appearance),
    pattern: str(p?.pattern),
    tags: (p?.tags || []).join(', '),
});

const positive = (v: string) => {
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The body the API is sent. Empty fields are left out, except where the product
 * had a value and the person cleared it: that is sent as an empty value, so a
 * wrong finish can be removed (the API answers if it refuses, and the dialog
 * shows it).
 */
export function draftToInput(d: Draft, mode: 'create' | 'edit', original?: NetworkProduct | null): ProductInput {
    const width = positive(d.width);
    const height = positive(d.height);
    const depth = positive(d.depth);
    const input: ProductInput = { name: d.name.trim() };
    const had = (field: keyof NetworkProduct) => mode === 'edit' && !!original && original[field] !== undefined && original[field] !== null && original[field] !== '';
    const put = (field: 'ref' | 'finish' | 'material' | 'appearance' | 'pattern', value: string) => {
        const v = value.trim();
        if (v || had(field)) input[field] = v;
    };
    put('ref', d.ref);
    put('finish', d.finish);
    put('material', d.material);
    put('appearance', d.appearance);
    put('pattern', d.pattern);
    const eans = splitList(d.eans);
    if (eans.length || (mode === 'edit' && original?.eans?.length)) input.eans = eans;
    const tags = splitList(d.tags);
    if (tags.length || (mode === 'edit' && original?.tags?.length)) input.tags = tags;
    if (width && height) input.format = { width, height, ...(depth ? { depth } : {}), unit: d.unit || 'cm' };
    if (d.colourName.trim() || d.colourHex.trim() || d.colourFamily) {
        input.colour = {
            name: d.colourName.trim(),
            ...(d.colourHex.trim() ? { hex: d.colourHex.trim() } : {}),
            ...(d.colourFamily ? { family: d.colourFamily } : {}),
        };
    }
    if (d.collectionId) input.collectionId = d.collectionId;
    else if (mode === 'edit' && original?.collectionId) input.collectionId = null;
    return input;
}

function VocabSelect({ id, value, values, onChange }: { id: string; value: string; values: readonly string[]; onChange: (v: string) => void }) {
    const { t } = useTranslation();
    // A value outside the vocabulary (older data) stays visible rather than silently blank.
    const options = value && !values.includes(value) ? [value, ...values] : values;
    return (
        <NativeSelect id={id} className="w-full" value={value} onChange={(e) => onChange(e.target.value)}>
            <NativeSelectOption value="">{t('ADMIN_NETWORK.FIELDS.NONE')}</NativeSelectOption>
            {options.map((v) => (
                <NativeSelectOption key={v} value={v}>{vocabLabel(t, v)}</NativeSelectOption>
            ))}
        </NativeSelect>
    );
}

/**
 * A network product's attributes. Edit mode PATCHes the product; create mode
 * POSTs a new one under `brandId`. Materials, appearances, finishes and colour
 * families are the closed vocabularies — the API would snap anything else.
 */
export function ProductEditDialog({
    open,
    onOpenChange,
    mode,
    product,
    brandId,
    collections,
    defaultCollectionId,
    onSaved,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
    product?: NetworkProduct | null;
    brandId: string;
    collections: NetworkCollection[];
    defaultCollectionId?: string;
    onSaved: (product: NetworkProduct) => void;
}) {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const [draft, setDraft] = useState<Draft>(() => toDraft(product, defaultCollectionId));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setDraft(toDraft(mode === 'edit' ? product : null, defaultCollectionId));
        setError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const set = (field: keyof Draft) => (value: string) => setDraft((d) => ({ ...d, [field]: value }));
    const sizeHalf = !!positive(draft.width) !== !!positive(draft.height);

    const save = async () => {
        if (!draft.name.trim() || sizeHalf) return;
        setBusy(true);
        setError(null);
        try {
            const input = draftToInput(draft, mode, product);
            const saved = mode === 'edit' && product
                ? await api.updateProduct(product._id, input)
                : await api.createProduct({ ...input, brandId, name: input.name });
            if (!saved?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            onOpenChange(false);
            onSaved(saved);
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    const text = (id: keyof Draft, label: string, props: React.ComponentProps<typeof Input> = {}) => (
        <div className="grid gap-1.5">
            <Label htmlFor={`pe-${id}`} className={FIELD_LABEL}>{label}</Label>
            <Input id={`pe-${id}`} value={draft[id]} onChange={(e) => set(id)(e.target.value)} {...props} />
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="product-edit-dialog">
                <DialogHeader>
                    <DialogTitle>{mode === 'edit' ? t('ADMIN_NETWORK.PRODUCT_EDIT.TITLE_EDIT') : t('ADMIN_NETWORK.PRODUCT_EDIT.TITLE_CREATE')}</DialogTitle>
                    <DialogDescription>{t('ADMIN_NETWORK.PRODUCT_EDIT.DESCRIPTION')}</DialogDescription>
                </DialogHeader>
                <form
                    className="grid gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void save();
                    }}
                >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {text('name', t('ADMIN_NETWORK.FIELDS.NAME'), { required: true, autoFocus: true })}
                        <div className="grid gap-1.5">
                            <Label htmlFor="pe-collection" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.COLLECTION')}</Label>
                            <NativeSelect id="pe-collection" className="w-full" value={draft.collectionId} onChange={(e) => set('collectionId')(e.target.value)}>
                                <NativeSelectOption value="">{t('ADMIN_NETWORK.FIELDS.NO_COLLECTION')}</NativeSelectOption>
                                {collections.map((c) => (
                                    <NativeSelectOption key={c._id} value={c._id}>{c.name}</NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>
                        {text('ref', t('ADMIN_NETWORK.FIELDS.REF'), { dir: 'ltr' })}
                        {text('eans', t('ADMIN_NETWORK.FIELDS.EANS'), { dir: 'ltr', placeholder: t('ADMIN_NETWORK.FIELDS.LIST_HINT'), inputMode: 'numeric' })}
                    </div>

                    <fieldset className="grid gap-1.5">
                        <legend className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.FORMAT')}</legend>
                        <div className="grid grid-cols-4 gap-2">
                            <Input aria-label={t('ADMIN_NETWORK.FIELDS.WIDTH')} placeholder={t('ADMIN_NETWORK.FIELDS.WIDTH')} inputMode="decimal" value={draft.width} onChange={(e) => set('width')(e.target.value)} className="tabular-nums" />
                            <Input aria-label={t('ADMIN_NETWORK.FIELDS.HEIGHT')} placeholder={t('ADMIN_NETWORK.FIELDS.HEIGHT')} inputMode="decimal" value={draft.height} onChange={(e) => set('height')(e.target.value)} className="tabular-nums" />
                            <Input aria-label={t('ADMIN_NETWORK.FIELDS.DEPTH')} placeholder={t('ADMIN_NETWORK.FIELDS.DEPTH')} inputMode="decimal" value={draft.depth} onChange={(e) => set('depth')(e.target.value)} className="tabular-nums" />
                            <NativeSelect aria-label={t('ADMIN_NETWORK.FIELDS.UNIT')} className="w-full" value={draft.unit} onChange={(e) => set('unit')(e.target.value)}>
                                {UNITS.map((u) => (
                                    <NativeSelectOption key={u} value={u}>{u}</NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>
                        {sizeHalf && <p className="text-xs text-destructive">{t('ADMIN_NETWORK.PRODUCT_EDIT.SIZE_BOTH')}</p>}
                    </fieldset>

                    <fieldset className="grid gap-1.5">
                        <legend className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.COLOUR')}</legend>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <Input aria-label={t('ADMIN_NETWORK.FIELDS.COLOUR_NAME')} placeholder={t('ADMIN_NETWORK.FIELDS.COLOUR_NAME')} value={draft.colourName} onChange={(e) => set('colourName')(e.target.value)} />
                            <div className="flex items-center gap-2">
                                <Input aria-label={t('ADMIN_NETWORK.FIELDS.COLOUR_HEX')} placeholder="#D9D4CC" dir="ltr" value={draft.colourHex} onChange={(e) => set('colourHex')(e.target.value)} />
                                <ColourSwatch hex={draft.colourHex} size="md" />
                            </div>
                            <VocabSelect id="pe-colour-family" value={draft.colourFamily} values={NETWORK_COLOR_FAMILIES} onChange={set('colourFamily')} />
                        </div>
                    </fieldset>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="pe-material" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.MATERIAL')}</Label>
                            <VocabSelect id="pe-material" value={draft.material} values={NETWORK_MATERIALS} onChange={set('material')} />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="pe-appearance" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.APPEARANCE')}</Label>
                            <VocabSelect id="pe-appearance" value={draft.appearance} values={NETWORK_APPEARANCES} onChange={set('appearance')} />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="pe-finish" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.FINISH')}</Label>
                            <VocabSelect id="pe-finish" value={draft.finish} values={NETWORK_FINISHES} onChange={set('finish')} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {text('pattern', t('ADMIN_NETWORK.FIELDS.PATTERN'))}
                        {text('tags', t('ADMIN_NETWORK.FIELDS.TAGS'), { placeholder: t('ADMIN_NETWORK.FIELDS.LIST_HINT') })}
                    </div>

                    {error && <ErrorNote message={error} />}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                            {t('ADMIN_SHELL.COMMON.CANCEL')}
                        </Button>
                        <Button type="submit" variant="outline" disabled={busy || !draft.name.trim() || sizeHalf} data-testid="product-edit-save">
                            {busy ? t('ADMIN_NETWORK.COMMON.SAVING') : mode === 'edit' ? t('ADMIN_SHELL.COMMON.SAVE') : t('ADMIN_NETWORK.PRODUCT_EDIT.CREATE')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
